import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { flashcardService } from '../../services/flashcard.service';
import { FlashcardSet, Flashcard } from '../../types/flashcard.types';
import { ArrowLeft, Volume2, ChevronLeft, ChevronRight, RotateCw, Check, X } from 'lucide-react';

const LearnMode: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if accessed from student page
  const isStudentMode = location.pathname.startsWith('/student/flashcards');

  const [set, setSet] = useState<FlashcardSet | null>(null);
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [masteredCards, setMasteredCards] = useState<Set<number>>(new Set());
  const [difficultCards, setDifficultCards] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadSet();
  }, [id]);

  const loadSet = async () => {
    try {
      setLoading(true);
      const response = await flashcardService.getSetById(Number(id));
      setSet(response.data);
      setCards(response.data.cards || []);
    } catch (error) {
      console.error('Failed to load set:', error);
      alert('Không thể tải set!');
    } finally {
      setLoading(false);
    }
  };

  const currentCard = cards[currentIndex];
  const progress = cards.length > 0 ? ((currentIndex + 1) / cards.length) * 100 : 0;

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleShuffle = () => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleMarkMastered = () => {
    if (currentCard) {
      const newMastered = new Set(masteredCards);
      newMastered.add(currentCard.id);
      setMasteredCards(newMastered);
      difficultCards.delete(currentCard.id);
      handleNext();
    }
  };

  const handleMarkDifficult = () => {
    if (currentCard) {
      const newDifficult = new Set(difficultCards);
      newDifficult.add(currentCard.id);
      setDifficultCards(newDifficult);
      masteredCards.delete(currentCard.id);
      handleNext();
    }
  };

  const playAudio = () => {
    if (currentCard?.audio_url) {
      const audio = new Audio(currentCard.audio_url);
      audio.play();
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFlip();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, isFlipped]);

  if (loading) {
    return <LoadingContainer>Đang tải...</LoadingContainer>;
  }

  if (!set || cards.length === 0) {
    return (
      <EmptyContainer>
        <EmptyText>Set này chưa có flashcard nào</EmptyText>
        <BackButton onClick={() => navigate(isStudentMode ? '/student/flashcards' : '/flashcards')}>
          <ArrowLeft size={20} />
          Quay lại
        </BackButton>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(isStudentMode ? '/student/flashcards' : '/flashcards')}>
          <ArrowLeft size={20} />
          Quay lại
        </BackButton>
        <SetInfo>
          <SetName>{set.name}</SetName>
          <CardCounter>
            {currentIndex + 1} / {cards.length}
          </CardCounter>
        </SetInfo>
        <ShuffleButton onClick={handleShuffle}>
          <RotateCw size={20} />
          Xáo trộn
        </ShuffleButton>
      </Header>

      <ProgressBar>
        <ProgressFill progress={progress} />
      </ProgressBar>

      <Stats>
        <StatItem color="#48bb78">
          <Check size={16} />
          Đã thuộc: {masteredCards.size}
        </StatItem>
        <StatItem color="#f56565">
          <X size={16} />
          Khó: {difficultCards.size}
        </StatItem>
      </Stats>

      <FlashcardContainer>
        <NavigationButton 
          onClick={handlePrevious} 
          disabled={currentIndex === 0}
          position="left"
        >
          <ChevronLeft size={32} />
        </NavigationButton>

        <FlashcardWrapper onClick={handleFlip}>
          <Flashcard3D flipped={isFlipped}>
            <CardFace side="front">
              <CardContent>
                <FrontText>{currentCard.front}</FrontText>
                {currentCard.phonetic && (
                  <PhoneticText>
                    {currentCard.phonetic}
                    {currentCard.audio_url && (
                      <AudioButton onClick={(e) => { e.stopPropagation(); playAudio(); }}>
                        <Volume2 size={20} />
                      </AudioButton>
                    )}
                  </PhoneticText>
                )}
                {currentCard.image_url && (
                  <CardImage src={currentCard.image_url} alt={currentCard.front} />
                )}
              </CardContent>
              <FlipHint>Click để xem nghĩa →</FlipHint>
            </CardFace>

            <CardFace side="back">
              <CardContent>
                <BackText>{currentCard.back}</BackText>
                {currentCard.example && (
                  <ExampleText>
                    <strong>Ví dụ:</strong> {currentCard.example}
                  </ExampleText>
                )}
                {currentCard.note && (
                  <NoteText>
                    <strong>Ghi chú:</strong> {currentCard.note}
                  </NoteText>
                )}
              </CardContent>
              <FlipHint>Click để xem từ ←</FlipHint>
            </CardFace>
          </Flashcard3D>
        </FlashcardWrapper>

        <NavigationButton 
          onClick={handleNext} 
          disabled={currentIndex === cards.length - 1}
          position="right"
        >
          <ChevronRight size={32} />
        </NavigationButton>
      </FlashcardContainer>

      {isFlipped && (
        <Actions>
          <ActionButton color="#f56565" onClick={handleMarkDifficult}>
            <X size={24} />
            Khó (cần học lại)
          </ActionButton>
          <ActionButton color="#48bb78" onClick={handleMarkMastered}>
            <Check size={24} />
            Đã thuộc
          </ActionButton>
        </Actions>
      )}

      <KeyboardHints>
        <Hint>← → Chuyển thẻ</Hint>
        <Hint>Space/Enter Lật thẻ</Hint>
      </KeyboardHints>
    </Container>
  );
};

// Animations
const flipAnimation = keyframes`
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(180deg);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #edf2f7;
  color: #2d3748;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    transform: translateY(-2px);
  }
`;

const SetInfo = styled.div`
  text-align: center;
  flex: 1;
`;

const SetName = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

const CardCounter = styled.div`
  font-size: 1.125rem;
  color: #718096;
  font-weight: 600;
`;

const ShuffleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
`;

const Stats = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const StatItem = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  color: ${props => props.color};

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    gap: 0.3rem;
  }
`;

const FlashcardContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  perspective: 1000px;
  margin: 2rem 0;

  @media (max-width: 768px) {
    gap: 1rem;
    margin: 1.5rem 0;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin: 1rem 0;
  }
`;

const NavigationButton = styled.button<{ position: 'left' | 'right' }>`
  padding: 1rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  color: #667eea;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f7fafc;
    border-color: #667eea;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const FlashcardWrapper = styled.div`
  width: 100%;
  max-width: 700px;
  aspect-ratio: 3 / 2;
  cursor: pointer;
`;

const Flashcard3D = styled.div<{ flipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  transform: ${props => props.flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const CardFace = styled.div<{ side: 'front' | 'back' }>`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  ${props => props.side === 'back' && `
    transform: rotateY(180deg);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  `}

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const FrontText = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
  }
`;

const PhoneticText = styled.div`
  font-size: 1.5rem;
  color: #718096;
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1rem;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
`;

const AudioButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 50%;
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem;
  }
`;

const CardImage = styled.img`
  max-width: 100%;
  max-height: 250px;
  object-fit: cover;
  border-radius: 12px;
  margin-top: 1rem;

  @media (max-width: 768px) {
    max-height: 150px;
    margin-top: 0.75rem;
  }

  @media (max-width: 480px) {
    max-height: 100px;
    margin-top: 0.5rem;
  }
`;

const BackText = styled.div`
  font-size: 3rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 0.75rem;
  }
`;

const ExampleText = styled.div`
  font-size: 1.125rem;
  text-align: center;
  margin-top: 1rem;
  opacity: 0.9;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-top: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-top: 0.5rem;
  }
`;

const NoteText = styled.div`
  font-size: 1rem;
  text-align: center;
  margin-top: 1rem;
  opacity: 0.8;
`;

const FlipHint = styled.div`
  font-size: 0.875rem;
  color: #a0aec0;
  font-style: italic;
  margin-top: auto;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ActionButton = styled.button<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: ${props => props.color};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 15px ${props => props.color}40;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px ${props => props.color}60;
  }
`;

const KeyboardHints = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
`;

const Hint = styled.div`
  font-size: 0.875rem;
  color: #a0aec0;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.5rem;
  color: #718096;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  gap: 2rem;
`;

const EmptyText = styled.div`
  font-size: 1.5rem;
  color: #718096;
`;

export default LearnMode;
